import { useEffect, useMemo, useRef } from "react";
import { Badge, QueryStateBoundary } from "@/components/ui";
import type { MessageOut } from "@/api/types/studio";
import { pageLabel } from "@/api/types/studio";
import { useStudioMessages, useStudioSummary } from "@/api/hooks/useStudio";
import { waitingForAssistant } from "./waiting";

/** Sticky-bottom threshold: within 80px of the bottom counts as "pinned". */
const PINNED_THRESHOLD_PX = 80;

function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatBytes(size: number): string {
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
}

function Attachment({ url, filename, contentType, sizeBytes }: {
  url: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
}) {
  if (contentType.startsWith("image/")) {
    return (
      <button
        type="button"
        onClick={() => window.open(url, "_blank", "noopener")}
        className="block rounded-md ring-1 ring-border-strong/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label={`Open ${filename}`}
        title={filename}
      >
        {/* url is the server-provided same-origin /api path; the session
            cookie authenticates the <img> and the opened tab alike. */}
        <img
          src={url}
          alt={filename}
          loading="lazy"
          className="h-24 w-24 rounded-md object-cover"
        />
      </button>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex max-w-[16rem] items-center gap-2 rounded-md border border-border bg-bg-body px-2.5 py-1.5 text-xs text-text-primary hover:border-border-strong hover:bg-bg-hover"
    >
      <svg className="h-4 w-4 shrink-0 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12l-3.75 3.75m0 0l-3.75-3.75M12 18.75V9" />
      </svg>
      <span className="truncate">{filename}</span>
      <span className="shrink-0 text-text-muted">{formatBytes(sizeBytes)}</span>
    </a>
  );
}

function MessageRow({ message: m }: { message: MessageOut }) {
  // Approval/system rows are renderings of ask decisions and bridge notes,
  // not chat — the contract wants them as centered muted lines.
  if (m.kind === "approval" || m.kind === "system") {
    return (
      <div className="my-1 flex flex-col items-center" data-testid="studio-system-row">
        <p className="max-w-[85%] text-center text-xs text-text-muted italic">{m.body}</p>
        <p className="mt-0.5 text-[10px] text-text-muted/70">{formatTime(m.created_at)}</p>
      </div>
    );
  }

  const own = m.author !== "assistant";
  const isFeedback = m.kind === "feedback";

  const bubbleClasses = isFeedback
    ? "rounded-xl border border-copper/40 bg-bg-card text-text-primary"
    : own
      ? "rounded-2xl rounded-br-sm bg-copper text-white"
      : "rounded-2xl rounded-bl-sm border border-border bg-bg-card text-text-primary";

  return (
    <div className={`flex flex-col ${own ? "items-end" : "items-start"}`} data-studio-message={m.id}>
      {isFeedback && (
        <div className={`mb-1 flex items-center gap-2 ${own ? "flex-row-reverse" : ""}`}>
          <Badge variant="primary" size="sm">Feedback</Badge>
          {m.page && <span className="text-xs text-text-muted">{pageLabel(m.page)}</span>}
        </div>
      )}
      <div className={`max-w-[85%] px-3.5 py-2 text-sm shadow-xs ${bubbleClasses}`}>
        {/* Photo-only messages have an empty body: skip the blank line. */}
        {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
        {m.attachments.length > 0 && (
          <div className={`${m.body ? "mt-2 " : ""}flex flex-wrap gap-2`}>
            {m.attachments.map((a) => (
              <Attachment
                key={a.id}
                url={a.url}
                filename={a.filename}
                contentType={a.content_type}
                sizeBytes={a.size_bytes}
              />
            ))}
          </div>
        )}
        <p className={`mt-1 text-[10px] ${own && !isFeedback ? "text-white/70" : "text-text-muted"}`}>
          {formatTime(m.created_at)}
        </p>
      </div>
    </div>
  );
}

/**
 * "On it" indicator: an assistant-style bubble with pulsing dots shown while
 * the thread's newest message is still a person's and the bridge hasn't
 * consumed it yet. `motion-safe:` keeps the dots static under
 * prefers-reduced-motion.
 */
function OnItBubble() {
  return (
    <div className="flex flex-col items-start" data-testid="studio-on-it" aria-live="polite">
      <div className="flex max-w-[85%] items-center gap-2.5 rounded-2xl rounded-bl-sm border border-border bg-bg-card px-3.5 py-2 text-sm text-text-secondary shadow-xs">
        <span className="flex items-center gap-1" aria-hidden="true">
          {[0, 150, 300].map((delay) => (
            <span
              key={delay}
              style={{ animationDelay: `${delay}ms` }}
              className="motion-safe:animate-pulse h-1.5 w-1.5 rounded-full bg-text-muted"
            />
          ))}
        </span>
        <span>Assistant is on it</span>
      </div>
    </div>
  );
}

/**
 * The one Studio thread: newest page first, "Load earlier" at the top,
 * stuck to the bottom on new messages unless the reader scrolled up.
 */
export function Thread() {
  const { data, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage, refetch } =
    useStudioMessages();
  const { data: summary } = useStudioSummary();

  const scrollRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true);

  // Pages arrive newest-page-first; render oldest→newest overall.
  const messages = useMemo(
    () => (data?.pages ?? []).slice().reverse().flatMap((page) => page.items),
    [data],
  );
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null;
  const waiting = waitingForAssistant(messages, summary);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !pinnedRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [lastMessageId, waiting]);

  async function loadEarlier() {
    const el = scrollRef.current;
    const prevHeight = el?.scrollHeight ?? 0;
    pinnedRef.current = false;
    await fetchNextPage();
    // Keep the viewport anchored on the messages being read: after older
    // pages prepend, shift scrollTop by the grown height.
    requestAnimationFrame(() => {
      if (el) el.scrollTop += el.scrollHeight - prevHeight;
    });
  }

  function handleScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < PINNED_THRESHOLD_PX;
  }

  return (
    <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      <div className="flex h-full min-h-0 flex-col">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          data-testid="studio-thread"
          className="flex-1 space-y-2.5 overflow-y-auto px-1 py-3"
        >
          {hasNextPage && (
            <div className="flex justify-center pb-2">
              <button
                type="button"
                onClick={() => void loadEarlier()}
                disabled={isFetchingNextPage}
                className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-bg-hover disabled:opacity-50"
              >
                {isFetchingNextPage ? "Loading…" : "Load earlier"}
              </button>
            </div>
          )}
          {messages.map((m) => (
            <MessageRow key={m.id} message={m} />
          ))}
          {waiting && <OnItBubble />}
          {messages.length === 0 && (
            <p className="py-10 text-center text-sm text-text-muted">
              Say hello — Nikki and Will's messages to the assistant live here.
            </p>
          )}
        </div>
      </div>
    </QueryStateBoundary>
  );
}
